// ==========================================================================
// HOME — Logo intro animation + hover nav
// ==========================================================================

import { gsap } from 'gsap';

// ── Nav color helper ─────────────────────────────────────────────────────────
// Reads data-color-left / data-color-right from the active slide and pushes
// the matching values into the CSS custom properties on #home-nav.

const COLOR_MAP = { white: '#f0f0f0', black: '#000000' };

function applyNavColors(swiper, homeNav) {
  const slide = swiper.slides[swiper.activeIndex];
  if (!slide) return;
  const left  = COLOR_MAP[slide.dataset.colorLeft]  ?? COLOR_MAP.white;
  const right = COLOR_MAP[slide.dataset.colorRight] ?? COLOR_MAP.white;

  // Sur mobile : une seule couleur (colonne du haut = gauche), pas de split
  const mob   = window.matchMedia('(max-width: 768px)').matches;
  homeNav.style.setProperty('--hn-left-color',  left);
  homeNav.style.setProperty('--hn-right-color', mob ? left : right);

  // Counter lives outside #home-nav so it can't inherit the CSS var — set directly.
  // Always uses the right/bottom column color (bottom column on mobile, right on desktop).
  const counter = document.querySelector('.slide__counter');
  if (counter) counter.style.color = right;
}

// ── Mobile helper ─────────────────────────────────────────────────────────────

const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

// ── Hover — reveal/hide nav with a small debounce to avoid vibration ──────────
// A shared timer is cleared by the opposite event, so rapid enter/leave pairs
// that happen within HOVER_DELAY ms are ignored entirely.

const HOVER_DELAY = 80; // ms — tune if needed

function wireHoverNav(homeNav, hnLogo, hnLeft, hnRight, getIsReady) {
  let timer = null;

  homeNav.addEventListener('mouseenter', () => {
    if (!getIsReady()) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      gsap.to(hnLogo,  { width: '16.666%', duration: 0.5, ease: 'power2.out' });
      gsap.to(hnLeft,  { x: 0, opacity: 1,  duration: 0.4, ease: 'power2.out' });
      gsap.to(hnRight, { x: 0, opacity: 1,  duration: 0.4, ease: 'power2.out' });
    }, HOVER_DELAY);
  });

  homeNav.addEventListener('mouseleave', () => {
    if (!getIsReady()) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      gsap.to(hnLogo,  { width: '83.333%',  duration: 0.6, ease: 'power2.in' });
      gsap.to(hnLeft,  { x: '-150%', opacity: 0, duration: 0.4, ease: 'power2.in' });
      gsap.to(hnRight, { x: '150%',  opacity: 0, duration: 0.4, ease: 'power2.in' });
    }, HOVER_DELAY);
  });
}

// ── Main init ─────────────────────────────────────────────────────────────────

export function initHome(swiper) {
  const homeNav  = document.getElementById('home-nav');
  if (!homeNav) return;

  const hnLogo      = homeNav.querySelector('.hn-logo');
  const fragments   = homeNav.querySelectorAll('.hn-logo .logo__fragment');
  const hnLeft      = homeNav.querySelector('.hn-left');
  const hnRight     = homeNav.querySelector('.hn-right');
  const hnBurger    = homeNav.querySelector('.hn-burger');

  if (!hnLogo || !fragments.length) return;

  const homeSlider = document.querySelector('.home-slider');
  const mobile     = isMobile();

  // ── Nav colors — on every slide change ───────────────────────────────────────
  // Note: the initial applyNavColors call is deferred:
  //   • skip-intro path  → called immediately below (slider already visible)
  //   • intro path       → called via tl.call() once the slider finishes fading in
  // This ensures the logo/menu stay white on the brown background during the intro,
  // even when the first slide requests black text.

  if (swiper) {
    swiper.on('slideChange', () => {
      if (isReady) applyNavColors(swiper, homeNav);
    });
  }

  // ── Skip intro if already played this session ─────────────────────────────────
  // Instead of jumping straight to the final state, replay only the last beat:
  // nav + logo at normal size → logo expands to full width (+ hover wired up).

  let isReady = false;

  if (sessionStorage.getItem('home-intro-played')) {
    if (homeSlider) gsap.set(homeSlider, { opacity: 1 });
    if (swiper) applyNavColors(swiper, homeNav);

    if (mobile) {
      // Mobile: no expansion — just show everything instantly
      gsap.set(hnLogo, { x: 0, y: 0 });
      if (hnBurger) gsap.set(hnBurger, { opacity: 1 });
      isReady = true;
      homeNav.classList.add('is-ready');
    } else {
      // Desktop: start from "nav open" state, then replay the expansion
      gsap.set(hnLogo,  { x: 0, y: 0, width: '16.666%' });
      gsap.set(hnLeft,  { x: 0, opacity: 1 });
      gsap.set(hnRight, { x: 0, opacity: 1 });

      gsap.timeline({ delay: 0.3 })
        .to(hnLogo,  { width: '83.333%', duration: 0.6, ease: 'power2.inOut' })
        .to(hnLeft,  { x: '-150%', opacity: 0, duration: 0.5, ease: 'power2.in' }, '<')
        .to(hnRight, { x: '150%',  opacity: 0, duration: 0.5, ease: 'power2.in' }, '<')
        .call(() => {
          isReady = true;
          homeNav.classList.add('is-ready');
        });

      wireHoverNav(homeNav, hnLogo, hnLeft, hnRight, () => isReady);
    }

    return;
  }

  // ── Set initial state ───────────────────────────────────────────────────────

  // Slider hidden — brown body background shows through until reveal
  if (homeSlider) gsap.set(homeSlider, { opacity: 0 });
  // Hide all fragments — fragment 0 appears first, then 1-3 one by one
  gsap.set(fragments, { opacity: 0 });

  if (mobile) {
    // On mobile: hide burger initially (burger revealed at end of animation)
    if (hnBurger) gsap.set(hnBurger, { opacity: 0 });
  } else {
    // Hide left/right nav on desktop
    gsap.set([hnLeft, hnRight], { opacity: 0 });
  }

  // Center logo visually in viewport
  const rect = hnLogo.getBoundingClientRect();
  const offsetX = window.innerWidth  / 2 - rect.left - rect.width  / 2;
  const offsetY = window.innerHeight / 2 - rect.top  - rect.height / 2;
  gsap.set(hnLogo, { x: offsetX, y: offsetY });

  // ── Timeline ─────────────────────────────────────────────────────────────────

  const tl = gsap.timeline({
    delay: 0.5,
    onComplete: () => {
      isReady = true;
      homeNav.classList.add('is-ready');
      sessionStorage.setItem('home-intro-played', '1');
    },
  });

  // Step 1 — logo fragments appear one by one
  tl.to(fragments[0], { opacity: 1, duration: 0.4, ease: 'none' }, 0)
    .to(fragments[1], { opacity: 1, duration: 0.4, ease: 'none' }, 0.5)
    .to(fragments[2], { opacity: 1, duration: 0.4, ease: 'none' }, 1.0)
    .to(fragments[3], { opacity: 1, duration: 0.4, ease: 'none' }, 1.5);

  // Step 2 — slider fades in once all 4 fragments are visible
  if (homeSlider) {
    tl.to(homeSlider, { opacity: 1, duration: 0.7, ease: 'power2.out' }, 2.5);
  }

  // Step 2b — apply first-slide nav colors only once the slider is fully visible
  // (before this point the brown body background shows through, so we stay white)
  if (swiper) {
    tl.call(() => applyNavColors(swiper, homeNav), [], 3.2);
  }

  // Step 3 — desktop only: left/right nav links appear
  if (!mobile) {
    tl.to([hnLeft, hnRight], { opacity: 1, duration: 0.5 }, 2.0);
  }

  // Step 4 — logo moves to its top position
  tl.to(hnLogo, { x: 0, y: 0, duration: 0.8, ease: 'power2.inOut' }, 2.2);

  if (mobile) {
    // Step 5 mobile — burger appears
    if (hnBurger) {
      tl.to(hnBurger, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 3.0);
    }
  } else {
    // Step 5 desktop — logo expands (10/12 cols = 83.333%), left/right fly off screen
    tl.to(hnLogo,  { width: '83.333%', duration: 0.6, ease: 'power2.inOut' }, 3.2)
      .to(hnLeft,  { x: '-150%', opacity: 0, duration: 0.5, ease: 'power2.in' }, 3.2)
      .to(hnRight, { x: '150%',  opacity: 0, duration: 0.5, ease: 'power2.in' }, 3.2);
  }

  // ── Hover — reveal nav (desktop only) ────────────────────────────────────────

  if (!mobile) {
    wireHoverNav(homeNav, hnLogo, hnLeft, hnRight, () => isReady);
  }

  // ── Click to skip — jump to final phase while animation is playing ────────────

  function skipToEnd() {
    if (isReady) return;
    tl.kill();

    if (swiper) applyNavColors(swiper, homeNav);
    if (homeSlider) gsap.set(homeSlider, { opacity: 1 });
    gsap.set(fragments, { opacity: 1 });
    sessionStorage.setItem('home-intro-played', '1');

    if (mobile) {
      gsap.set(hnLogo, { x: 0, y: 0 });
      if (hnBurger) gsap.set(hnBurger, { opacity: 1 });
      isReady = true;
      homeNav.classList.add('is-ready');
    } else {
      gsap.set(hnLogo,  { x: 0, y: 0, width: '16.666%' });
      gsap.set(hnLeft,  { x: 0, opacity: 1 });
      gsap.set(hnRight, { x: 0, opacity: 1 });

      gsap.timeline()
        .to(hnLogo,  { width: '83.333%', duration: 0.6, ease: 'power2.inOut' })
        .to(hnLeft,  { x: '-150%', opacity: 0, duration: 0.5, ease: 'power2.in' }, '<')
        .to(hnRight, { x: '150%',  opacity: 0, duration: 0.5, ease: 'power2.in' }, '<')
        .call(() => {
          isReady = true;
          homeNav.classList.add('is-ready');
        });
    }
  }

  document.addEventListener('click', skipToEnd, { once: true });
}
